// pages/VisitNotifications.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  AlertCircle,
  CheckCircle,
  Clock,
  User,
  Building2,
  MessageSquare,
  Filter,
  Search,
  RefreshCw,
  Eye,
  Wrench,
  TrendingUp,
  Users,
} from "lucide-react";
import apiService from "../../services/api";
import { useRealtimeSync } from "../../hooks/useRealtimeSync";
import { useTheme } from "../../contexts/ThemeContext";

const VisitNotifications = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedPriority, setSelectedPriority] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusCounts, setStatusCounts] = useState({});
  const [stats, setStats] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // WebSocket real-time updates
  const handleRealtimeUpdate = useCallback((data) => {
    console.log("📡 Received visit notification update:", data);

    if (data.type === "visit_notification_updated") {
      const { action, data: notificationData } = data;

      setNotifications((prevNotifications) => {
        if (action === "created") {
          return [notificationData, ...prevNotifications];
        } else if (action === "status_updated") {
          return prevNotifications.map((notif) =>
            notif.id === notificationData.id
              ? { ...notif, ...notificationData }
              : notif,
          );
        } else if (action === "comment_added") {
          return prevNotifications.map((notif) => {
            if (notif.id === notificationData.notificationId) {
              return {
                ...notif,
                comments: [...(notif.comments || []), notificationData.comment],
              };
            }
            return notif;
          });
        }
        return prevNotifications;
      });

      fetchStats();
    }
  }, []);

  const { isConnected } = useRealtimeSync({
    events: ["visit_notification_updated"],
    onUpdate: handleRealtimeUpdate,
    enabled: true,
  });

  useEffect(() => {
    fetchNotifications();
    fetchStats();
  }, [selectedStatus, selectedPriority, searchTerm]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const params = {
        status: selectedStatus === "all" ? undefined : selectedStatus,
        priority: selectedPriority === "all" ? undefined : selectedPriority,
      };

      const response = await apiService.getVisitNotifications(params);
      setNotifications(response.data || []);
      setStatusCounts(response.statusCounts || {});
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiService.getVisitNotificationStats();
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleStatusUpdate = async (notificationId, status, notes = "") => {
    try {
      await apiService.updateVisitNotificationStatus(notificationId, {
        status,
        resolutionNotes: notes,
      });
      fetchNotifications();
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const handleNotificationClick = (notification) => {
    navigate(`/visit-notifications/${notification.id}`);
  };

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: "bg-red-500/20 text-red-400 border-red-500/30",
      high: "bg-orange-500/20 text-orange-400 border-orange-500/30",
      medium: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      low: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    };
    return colors[priority] || colors.medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      unread: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      read: "bg-gray-500/20 text-gray-400 border-gray-500/30",
      in_progress: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      resolved: "bg-green-500/20 text-green-400 border-green-500/30",
      dismissed: "bg-gray-500/20 text-gray-400 border-gray-500/30",
    };
    return colors[status] || colors.unread;
  };

  const getStatusIcon = (status) => {
    const icons = {
      unread: Bell,
      read: Eye,
      in_progress: Clock,
      resolved: CheckCircle,
      dismissed: AlertCircle,
    };
    return icons[status] || Bell;
  };

  const filteredNotifications = notifications.filter((notif) => {
    const matchesSearch =
      notif.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notif.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notif.visit?.account?.name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  const statuses = [
    { key: "all", label: "All", icon: Bell },
    { key: "unread", label: "Unread", icon: AlertCircle },
    { key: "in_progress", label: "In Progress", icon: Clock },
    { key: "resolved", label: "Resolved", icon: CheckCircle },
  ];

  const priorities = [
    { key: "all", label: "All Priorities" },
    { key: "urgent", label: "Urgent" },
    { key: "high", label: "High" },
    { key: "medium", label: "Medium" },
    { key: "low", label: "Low" },
  ];

  return (
    <div
      className={`min-h-screen font-inter transition-colors duration-300 ${isDarkMode ? "bg-[#0A0B0F] text-white" : "bg-[#F4F5F7] text-gray-900"}`}
    >
      {/* Header */}
      <div
        className={`border-b transition-colors ${isDarkMode ? "bg-gradient-to-r from-[#0C0E12] via-[#0E1015] to-[#0C0E12] border-gray-800/50" : "bg-white border-gray-200"}`}
      >
        <div className="max-w-[1800px] mx-auto px-6 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{
                    background:
                      "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
                  }}
                >
                  <Bell className="w-5 h-5 text-white" />
                </div>
                <h1
                  className={`text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Visit Notifications
                </h1>
                {isConnected && (
                  <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full border border-green-500/30">
                    Live
                  </span>
                )}
              </div>
              <p className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
                Outstanding work and issues from technical visits
              </p>
            </div>

            <button
              onClick={fetchNotifications}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-700/50 border border-gray-700/50 text-white" : "bg-gray-100 hover:bg-gray-200 border border-gray-200 text-gray-700"}`}
            >
              <RefreshCw size={18} />
              Refresh
            </button>
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div
                className={`rounded-xl p-4 ${isDarkMode ? "bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20" : "bg-white border border-blue-200 shadow-sm"}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span
                    className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Total Notifications
                  </span>
                  <Bell className="text-blue-400" size={20} />
                </div>
                <div
                  className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {notifications.length}
                </div>
              </div>

              <div
                className={`rounded-xl p-4 ${isDarkMode ? "bg-gradient-to-br from-red-500/10 to-red-600/5 border border-red-500/20" : "bg-white border border-red-200 shadow-sm"}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span
                    className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Unread
                  </span>
                  <AlertCircle className="text-red-400" size={20} />
                </div>
                <div
                  className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {statusCounts.unread || 0}
                </div>
              </div>

              <div
                className={`rounded-xl p-4 ${isDarkMode ? "bg-gradient-to-br from-yellow-500/10 to-yellow-600/5 border border-yellow-500/20" : "bg-white border border-yellow-200 shadow-sm"}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span
                    className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    In Progress
                  </span>
                  <Clock className="text-yellow-400" size={20} />
                </div>
                <div
                  className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {statusCounts.in_progress || 0}
                </div>
              </div>

              <div
                className={`rounded-xl p-4 ${isDarkMode ? "bg-gradient-to-br from-green-500/10 to-green-600/5 border border-green-500/20" : "bg-white border border-green-200 shadow-sm"}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span
                    className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Avg. Resolution Time
                  </span>
                  <TrendingUp className="text-green-400" size={20} />
                </div>
                <div
                  className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {stats.avgResolutionHours.toFixed(1)}h
                </div>
              </div>
            </div>
          )}

          {/* Status Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {statuses.map((status) => {
              const Icon = status.icon;
              return (
                <button
                  key={status.key}
                  onClick={() => setSelectedStatus(status.key)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all duration-200 whitespace-nowrap ${
                    selectedStatus === status.key
                      ? isDarkMode
                        ? "bg-blue-500/20 text-blue-400 border-blue-500/30"
                        : "bg-blue-100 text-blue-600 border-blue-200"
                      : isDarkMode
                        ? "bg-gray-800/30 border-gray-700/50 text-gray-400 hover:bg-gray-700/50"
                        : "bg-gray-100 border-gray-200 text-gray-500 hover:bg-gray-200"
                  }`}
                >
                  <Icon size={16} />
                  <span>{status.label}</span>
                  {status.key !== "all" && (
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${selectedStatus === status.key ? (isDarkMode ? "bg-white/10" : "bg-blue-200") : isDarkMode ? "bg-gray-700/50" : "bg-gray-200"}`}
                    >
                      {statusCounts[status.key] || 0}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="max-w-[1800px] mx-auto px-6 py-4">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search
              className={`absolute left-4 top-1/2 transform -translate-y-1/2 ${isDarkMode ? "text-gray-400" : "text-gray-400"}`}
              size={20}
            />
            <input
              type="text"
              placeholder="Search notifications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-12 pr-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${isDarkMode ? "bg-gray-900/50 border border-gray-700/50 text-white placeholder-gray-500 focus:border-blue-500/50" : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-400"}`}
            />
          </div>

          <select
            value={selectedPriority}
            onChange={(e) => setSelectedPriority(e.target.value)}
            className={`px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${isDarkMode ? "bg-gray-900/50 border border-gray-700/50 text-white focus:border-blue-500/50" : "bg-white border border-gray-300 text-gray-900 focus:border-blue-400"}`}
          >
            {priorities.map((priority) => (
              <option key={priority.key} value={priority.key}>
                {priority.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Notifications List */}
      <div className="max-w-[1800px] mx-auto px-6 pb-6">
        <div className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
                Loading notifications...
              </div>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="text-center py-20">
              <Bell
                className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? "text-gray-600" : "text-gray-300"}`}
              />
              <h3
                className={`text-xl font-semibold mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                No notifications found
              </h3>
              <p className={isDarkMode ? "text-gray-500" : "text-gray-400"}>
                {selectedStatus !== "all"
                  ? "Try adjusting your filters"
                  : "All clear! No outstanding work at the moment"}
              </p>
            </div>
          ) : (
            filteredNotifications.map((notification) => {
              const StatusIcon = getStatusIcon(notification.status);
              return (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`rounded-xl p-6 cursor-pointer transition-all ${isDarkMode ? "bg-[#13161B] border border-gray-700/50 hover:border-blue-500/50" : "bg-white border border-gray-200 shadow-sm hover:border-blue-300"}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Priority Indicator */}
                    <div
                      className={`w-1 h-full rounded-full ${
                        notification.priority === "urgent"
                          ? "bg-red-500"
                          : notification.priority === "high"
                            ? "bg-orange-500"
                            : notification.priority === "medium"
                              ? "bg-yellow-500"
                              : "bg-blue-500"
                      }`}
                    />

                    <div className="flex-1">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3
                              className={`font-semibold text-lg ${isDarkMode ? "text-white" : "text-gray-900"}`}
                            >
                              {notification.title}
                            </h3>
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(
                                notification.priority,
                              )}`}
                            >
                              {notification.priority.toUpperCase()}
                            </span>
                          </div>
                          <p
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }
                          >
                            {notification.message}
                          </p>
                        </div>

                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium border flex items-center gap-1 ${getStatusColor(
                            notification.status,
                          )}`}
                        >
                          <StatusIcon size={14} />
                          {notification.status.replace("_", " ").toUpperCase()}
                        </span>
                      </div>

                      {/* Visit Info */}
                      {notification.visit && (
                        <div
                          className={`grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-4 rounded-lg ${isDarkMode ? "bg-gray-800/30" : "bg-gray-50"}`}
                        >
                          <div className="flex items-center gap-2">
                            <Wrench
                              size={16}
                              className={
                                isDarkMode ? "text-gray-400" : "text-gray-500"
                              }
                            />
                            <div>
                              <div
                                className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                              >
                                Visit
                              </div>
                              <div
                                className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                              >
                                {notification.visit.visitName}
                              </div>
                            </div>
                          </div>

                          {notification.visit.account && (
                            <div className="flex items-center gap-2">
                              <Building2
                                size={16}
                                className={
                                  isDarkMode ? "text-gray-400" : "text-gray-500"
                                }
                              />
                              <div>
                                <div
                                  className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                                >
                                  Account
                                </div>
                                <div
                                  className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                >
                                  {notification.visit.account.name}
                                </div>
                              </div>
                            </div>
                          )}

                          {notification.visit.engineer && (
                            <div className="flex items-center gap-2">
                              <User
                                size={16}
                                className={
                                  isDarkMode ? "text-gray-400" : "text-gray-500"
                                }
                              />
                              <div>
                                <div
                                  className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                                >
                                  Engineer
                                </div>
                                <div
                                  className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                >
                                  {notification.visit.engineer.name}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Outstanding Work */}
                      {notification.outstandingWork && (
                        <div
                          className={`mb-4 p-4 rounded-lg ${isDarkMode ? "bg-orange-500/10 border border-orange-500/30" : "bg-orange-50 border border-orange-100"}`}
                        >
                          <div
                            className={`text-sm font-medium mb-1 ${isDarkMode ? "text-orange-400" : "text-orange-600"}`}
                          >
                            Outstanding Work:
                          </div>
                          <div
                            className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                          >
                            {notification.outstandingWork}
                          </div>
                        </div>
                      )}

                      {/* Footer */}
                      <div className="flex items-center justify-between text-sm">
                        <div
                          className={`flex items-center gap-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            {new Date(
                              notification.createdAt,
                            ).toLocaleDateString()}
                          </span>

                          {notification.comments &&
                            notification.comments.length > 0 && (
                              <span className="flex items-center gap-1">
                                <MessageSquare size={14} />
                                {notification.comments.length} comments
                              </span>
                            )}

                          {notification.assignedUser && (
                            <span className="flex items-center gap-1">
                              <User size={14} />
                              Assigned to {notification.assignedUser.name}
                            </span>
                          )}
                        </div>

                        {notification.status === "unread" && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStatusUpdate(
                                notification.id,
                                "in_progress",
                              );
                            }}
                            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                          >
                            Start Working
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default VisitNotifications;
